import React, { useContext, type ReactElement } from "react";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { ErrorDisplay } from "shared/components/Error";
import { Loader } from "shared/components/Loader";
import { getWorkspaceId, withWorkspaceId } from "shared/utils/workspaceRouting";
import { useAuth } from "lib/auth/AuthContext";
import { useLaunchDarkly } from "shared/components/useLaunchDarkly";
import { GlobalContext } from "../context/AppContextProvider";
import { Assessments } from "./assessments/Assessments";
import Custom from "./assessments/custom/Custom";
import NotFound from "./not-found";
import { Organization } from "./organization/Organizations";

const WorkspaceOrganizationRoute = (): ReactElement => {
  const { pathname, search } = useLocation();
  const { orgId } = useParams();
  const { orgState } = useContext(GlobalContext);
  const workspaceId = getWorkspaceId(pathname, search);
  if (workspaceId && pathname.startsWith("/app/organization/")) {
    return (
      <Navigate
        replace
        to={withWorkspaceId(`${pathname}${search}`, workspaceId)}
      />
    );
  }
  if (!workspaceId || orgState?.orgsError != null) {
    return (
      <ErrorDisplay
        title="No Organization selected"
        subTitle={
          workspaceId
            ? "Unable to load the selected organization in this workspace. Select it again in My Cropwise."
            : "Select a workspace and organization in My Cropwise to continue."
        }
      >
        <Link to="/my-cropwise">My Cropwise</Link>
      </ErrorDisplay>
    );
  }
  if (
    orgState?.isFetchingAllOrgs === true ||
    orgState?.currentOrg?.id !== orgId ||
    orgState?.workspaceId !== workspaceId
  ) {
    return <Loader mask />;
  }
  return <Outlet />;
};

export const AppRoutes = (): ReactElement | null => {
  const { pathname, search } = useLocation();
  const { orgState } = useContext(GlobalContext);
  const { user } = useAuth();
  const {
    flags: { notAllowedCountriesCode },
  } = useLaunchDarkly();

  if (!notAllowedCountriesCode || notAllowedCountriesCode.length === 0) {
    return null;
  }

  const renderOrganizationOrRedirect = (): ReactElement => {
    return IsAllowedCountry ? (
      <Organization />
    ) : (
      <Navigate replace to="/my-cropwise" />
    );
  };

  const IsAllowedCountry = notAllowedCountriesCode.includes(user?.country_code);
  const redirectToAssessmets = (): string => {
    const orgId = orgState?.currentOrg?.id;
    return orgId
      ? withWorkspaceId(
          `/app/organization/${orgId as string}/assessments`,
          getWorkspaceId(pathname, search)
        )
      : "/app";
  };

  return (
    <Routes>
      <Route path="/" element={renderOrganizationOrRedirect()} />
      <Route path="/app/">
        <Route path="" element={renderOrganizationOrRedirect()} />
        <Route
          path="assessments"
          element={<Navigate to={redirectToAssessmets()} />}
        />
        <Route
          path="workspace/:workspaceId"
          element={renderOrganizationOrRedirect()}
        />
        <Route
          path="workspace/:workspaceId/assessments"
          element={<Navigate to={redirectToAssessmets()} />}
        />
        {[
          "workspace/:workspaceId/organization/:orgId",
          "organization/:orgId",
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={<WorkspaceOrganizationRoute />}
          >
            <Route path="assessments" element={<Assessments />} />
            <Route path="assessment/create">
              <Route path="custom" element={<Custom />} />
              <Route path="custom/:customStep" element={<Custom />} />
              <Route key="not-found" path="" element={<NotFound />} />
            </Route>
            <Route path="assessment/edit/:assessmentId">
              <Route path="custom" element={<Custom />} />
              <Route path="custom/:customStep" element={<Custom />} />
              <Route key="not-found" path="" element={<NotFound />} />
            </Route>
          </Route>
        ))}
      </Route>
      <Route key="not-found" path="*" element={<NotFound />} />
    </Routes>
  );
};
